import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "wasp/client/auth";
import {
  getAllLocationTypes,
  deleteLocationType,
  useQuery,
  getLocationTypeById,
  updateLocationType,
  getPlatformSectionVisibility,
} from "wasp/client/operations";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { MoreHorizontal, Plus, Search, Package, Filter } from "lucide-react";
import DefaultLayout from "../../admin/layout/DefaultLayout";
import LoadingSpinner from "../../admin/layout/LoadingSpinner";
import { EnhancedActionButton, useResponsiveLayout } from "../../components/ui/enhanced-page-layout";
import { TableFilterLayout } from "../../components/ui/table-filter-layout";
import { AdminFlyoutPane } from "../../admin/components/AdminFlyoutPane";
import {
  FlyoutGridLayout,
  FlyoutGridSection,
  FlyoutGridRow,
} from "../../admin/components/FlyoutGridLayout";
import { toast } from "../../hooks/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Card, CardContent, CardHeader } from "../../components/ui/card";
import { SECTION_LABELS, SIDEBAR_SECTION_KEYS } from "./constants";

const AdminLocationTypesPage = () => {
  const { data: user } = useAuth();
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
  const [flyoutMode, setFlyoutMode] = useState<"view" | "edit">("view");
  const [search, setSearch] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState<string>("all");
  const { isFilterExpanded, setIsFilterExpanded } = useResponsiveLayout();

  // Editable fields
  const [editFormData, setEditFormData] = useState({
    name: "",
    description: "",
    iconName: "MapPin",
    color: "#64748B",
    isActive: true,
    displayOrder: 0,
    // Section visibility
    showNotice: true,
    showEvent: true,
    showFacility: true,
    showTenantBusiness: true,
    showMarketplaceItem: true,
    showService: true,
    showLostItem: true,
    showFoundItem: true,
    showAnnouncement: true,
    showBuySell: true,
    showFoodCourt: true,
    showRequestOffer: true,
    showRetail: true,
    showUrgentHelp: true,
    showAskForHelp: true,
    showShareWithNeighbors: true,
    showLostFound: true,
    showOtherRequests: true,
    showJob: true,
    showBuildingDocument: true,
    showWorkOrder: true,
    showBuildingSchedule: true,
    showBuildingSafety: true,
    showUpkeepRequest: true,
  });

  const { data, isLoading, error, refetch } = useQuery(getAllLocationTypes, {
    limit: 0,
    search: search || undefined,
    isActive: isActiveFilter === "all" ? undefined : isActiveFilter === "true",
  });

  const { data: selectedType, isLoading: loadingType } = useQuery(
    getLocationTypeById,
    { id: selectedTypeId || "" },
    { enabled: !!selectedTypeId, keepPreviousData: true },
  );

  const { data: platformVisibility } = useQuery(getPlatformSectionVisibility);
  const platformByKey =
    (platformVisibility as { byKey?: Record<string, boolean> } | undefined)
      ?.byKey ?? {};
  /** Only sections that are visible in global Section Visibility are listed in Location Types */
  const visibleSectionKeys = SIDEBAR_SECTION_KEYS.filter(
    (k) => platformByKey[k] !== false,
  );

  const typesData = data as
    | { locationTypes: any[]; total: number; page: number; totalPages: number }
    | undefined;
  const hasTypes = !!typesData?.locationTypes?.length;

  // Header (approx 6rem) + TableFilterLayout Columns (approx 2.5rem)
  // Collapsed: ~8.5rem + 1rem breathing room = 9.5rem
  // Expanded: ~8.5rem + Filters (~4rem) - adjusted to remove gap
  // Header (approx 6rem) + TableFilterLayout Columns (approx 2.5rem)
  // Collapsed: ~8.5rem + 1rem breathing room = 9.5rem
  // Expanded: ~8.5rem + Filters (~4rem) - adjusted to remove gap
  const contentPaddingTop = isFilterExpanded
    ? "pt-[1rem] md:pt-[12.5rem]"
    : "pt-[1rem] md:pt-[9.5rem]";

  // Debug logging
  useEffect(() => {
    if (error) {
      console.error("Location types query error:", error);
    }
    if (data) {
    }
  }, [data, error, isLoading]);

  // Type the selectedType properly
  const typedSelectedType = selectedType as any;

  // Initialize edit fields when type is selected
  useEffect(() => {
    if (typedSelectedType) {
      setEditFormData({
        name: typedSelectedType.name || "",
        description: typedSelectedType.description || "",
        iconName: typedSelectedType.iconName || "MapPin",
        color: typedSelectedType.color || "#64748B",
        isActive: typedSelectedType.isActive ?? true,
        displayOrder: typedSelectedType.displayOrder || 0,
        showNotice: typedSelectedType.showNotice ?? true,
        showEvent: typedSelectedType.showEvent ?? true,
        showFacility: typedSelectedType.showFacility ?? true,
        showTenantBusiness: typedSelectedType.showTenantBusiness ?? true,
        showMarketplaceItem: typedSelectedType.showMarketplaceItem ?? true,
        showService: typedSelectedType.showService ?? true,
        showLostItem: typedSelectedType.showLostItem ?? true,
        showFoundItem: typedSelectedType.showFoundItem ?? true,
        showAnnouncement: typedSelectedType.showAnnouncement ?? true,
        showBuySell: typedSelectedType.showBuySell ?? true,
        showFoodCourt: typedSelectedType.showFoodCourt ?? true,
        showRequestOffer: typedSelectedType.showRequestOffer ?? true,
        showRetail: typedSelectedType.showRetail ?? true,
        showUrgentHelp: typedSelectedType.showUrgentHelp ?? true,
        showAskForHelp: typedSelectedType.showAskForHelp ?? true,
        showShareWithNeighbors:
          typedSelectedType.showShareWithNeighbors ?? true,
        showLostFound: typedSelectedType.showLostFound ?? true,
        showOtherRequests: typedSelectedType.showOtherRequests ?? true,
        showJob: typedSelectedType.showJob ?? true,
        showBuildingDocument: typedSelectedType.showBuildingDocument ?? true,
        showWorkOrder: typedSelectedType.showWorkOrder ?? true,
        showBuildingSchedule: typedSelectedType.showBuildingSchedule ?? true,
        showBuildingSafety: typedSelectedType.showBuildingSafety ?? true,
        showUpkeepRequest: typedSelectedType.showUpkeepRequest ?? true,
      });
    }
  }, [typedSelectedType]);

  const handleTypeClick = (typeId: string) => {
    setSelectedTypeId(typeId);
    setFlyoutMode("view");
  };

  const handleEdit = () => {
    setFlyoutMode("edit");
  };

  const handleSave = async () => {
    if (!typedSelectedType) return;

    try {
      await updateLocationType({
        id: typedSelectedType.id,
        ...editFormData,
      });

      await refetch();
      setFlyoutMode("view");

      toast({
        title: "Success",
        description: "Location type updated successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update location type",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!typedSelectedType) return;

    if (
      confirm(
        "Are you sure you want to delete this location type? This action cannot be undone.",
      )
    ) {
      const idToDelete = typedSelectedType.id;
      try {
        setSelectedTypeId(null);
        await deleteLocationType({ id: idToDelete });
        await refetch();
        toast({
          title: "Success",
          description: "Location type deleted successfully",
        });
      } catch (error: any) {
        toast({
          title: "Error",
          description: error.message || "Failed to delete location type",
          variant: "destructive",
        });
        // Restore selection if delete failed
        setSelectedTypeId(idToDelete);
      }
    }
  };

  const countEnabledSections = (type: any) => {
    return visibleSectionKeys.filter((key) => type[key] === true).length;
  };

  if (!user) {
    return (
      <DefaultLayout user={null as any}>
        <div className="p-4 m-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="text-yellow-800 font-semibold">
            Authentication Required
          </div>
          <div className="text-yellow-600 mt-1">
            Please log in to access this page.
          </div>
        </div>
      </DefaultLayout>
    );
  }

  if (!user.isAdmin) {
    return (
      <DefaultLayout user={user}>
        <div className="p-4 m-4 bg-red-50 border border-red-200 rounded-md">
          <div className="text-red-800 font-semibold">Access Denied</div>
          <div className="text-red-600 mt-1">
            Admin privileges required to view location types.
          </div>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout user={user}>
      <div className="w-full">
        {/* Fixed Header - hidden on small screens; min-h keeps table headers below title */}
        <div className="hidden md:block fixed top-[var(--header-height)] left-[var(--sidebar-width)] right-0 z-30 bg-white dark:bg-sidebar-active transition-colors border-b border-border min-h-[6rem] flex flex-col justify-center">
          <div className="px-6 pt-6 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Location Types
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Manage location type definitions and section visibility (
                  {typesData?.total || 0} total)
                </p>
              </div>
              <div className="flex items-center gap-3">
                <EnhancedActionButton
                  onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                  icon={Filter}
                  label=""
                  className={isFilterExpanded ? "opacity-80" : ""}
                />
                <Link to="/admin/location-types/new">
                  <EnhancedActionButton
                    onClick={() => { }}
                    icon={Plus}
                    label="Create Type"
                  />
                </Link>
              </div>
            </div>
          </div>
        </div>

        <TableFilterLayout
          className="hidden md:block fixed top-[calc(var(--header-height)+6rem)] left-[var(--sidebar-width)] right-0 z-20"
          isExpanded={isFilterExpanded}
          columns={[
            { title: "Type Name", span: 4 },
            { title: "Slug", span: 2 },
            { title: "Sections Enabled", span: 3 },
            { title: "Locations", span: 1 },
            { title: "Status", span: 2 },
          ]}
          filters={[
            {
              span: 4,
              content: (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search name, slug, or description..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mr-4"
                  />
                </div>
              ),
            },
            { span: 2, content: <div className="h-10" /> },
            { span: 3, content: <div className="h-10" /> },
            { span: 1, content: <div className="h-10" /> },
            {
              span: 2,
              content: (
                <select
                  value={isActiveFilter}
                  onChange={(e) => setIsActiveFilter(e.target.value)}
                  className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mr-4"
                >
                  <option value="all">All</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              ),
            },
          ]}
        />

        {/* Scrollable Content - 4.5rem title + TableFilterLayout ~8rem — ensure first row is never hidden under fixed header */}
        <div
          className={`flex-1 overflow-y-auto transition-[padding] duration-300 ease-in-out ${contentPaddingTop}`}
        >
          {isLoading && <LoadingSpinner />}
          {error && (
            <div className="p-4 m-4 bg-red-50 border border-red-200 rounded-md">
              <div className="text-red-800 font-semibold">
                Error loading location types
              </div>
              <div className="text-red-600 mt-1">
                {error.message || "Unknown error occurred"}
              </div>
              <div className="text-red-500 text-sm mt-2">
                Status: {(error as any).status || "N/A"}
              </div>
            </div>
          )}

          {typesData?.locationTypes && typesData.locationTypes.length > 0 ? (
            <div className="w-full">
              {/* Card view - small screens only */}
              <div className="md:hidden px-4 space-y-4">
                {typesData.locationTypes.map((type: any) => (
                  <Card
                    key={type.id}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleTypeClick(type.id)}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {type.name}
                          </span>
                        </div>
                        <span
                          className={`text-sm font-medium ${type.isActive ? "text-green-600" : "text-gray-400"
                            }`}
                        >
                          {type.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                      {type.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mt-1">
                          {type.description}
                        </p>
                      )}
                    </CardHeader>
                    <CardContent className="pt-0 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          Slug
                        </span>
                        <span className="font-mono text-gray-700 dark:text-gray-300">
                          {type.slug}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          Sections
                        </span>
                        <span className="text-gray-700 dark:text-gray-300">
                          {countEnabledSections(type)} of{" "}
                          {visibleSectionKeys.length} enabled
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          Locations
                        </span>
                        <span className="text-gray-700 dark:text-gray-300">
                          {type._count?.locations || 0}
                        </span>
                      </div>
                      <div
                        className="flex justify-end pt-2"
                        onClick={(e) => e?.stopPropagation()}
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleTypeClick(type.id)}
                            >
                              View Details
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Table view - md and up */}
              <div className="hidden md:block divide-y divide-gray-200">
                {typesData.locationTypes.map((type: any) => (
                  <div
                    key={type.id}
                    className="admin-hoverable py-3 transition-colors cursor-pointer"
                    onClick={() => handleTypeClick(type.id)}
                  >
                    <div className="grid grid-cols-12 gap-4 items-center px-6">
                      {/* Type Name */}
                      <div className="col-span-4">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {type.name}
                          </div>
                        </div>
                        {type.description && (
                          <div className="text-sm text-gray-500 mt-1 line-clamp-1">
                            {type.description}
                          </div>
                        )}
                      </div>

                      {/* Slug */}
                      <div className="col-span-2">
                        <span className="text-sm text-gray-700 dark:text-gray-300 font-mono">
                          {type.slug}
                        </span>
                      </div>

                      {/* Sections Enabled */}
                      <div className="col-span-3">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {countEnabledSections(type)} of{" "}
                          {visibleSectionKeys.length} enabled
                        </span>
                      </div>

                      {/* Locations Count */}
                      <div className="col-span-1">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {type._count?.locations || 0}
                        </span>
                      </div>

                      {/* Status */}
                      <div className="col-span-2">
                        <div className="flex items-center justify-between">
                          <span
                            className={`text-sm font-medium ${type.isActive ? "text-green-600" : "text-gray-400"
                              }`}
                          >
                            {type.isActive ? "Active" : "Inactive"}
                          </span>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => e?.stopPropagation()}
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleTypeClick(type.id)}
                              >
                                View Details
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* No pagination - showing all location types */}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No location types found</p>
              <Link to="/admin/location-types/new">
                <Button className="mt-4">
                  Create your first location type
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Floating Create button - small screens only (header hidden in card view) */}
        <Link
          to="/admin/location-types/new"
          className="md:hidden fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-colors"
          aria-label="Create type"
        >
          <Plus className="h-6 w-6" />
        </Link>

        {/* Flyout Pane */}
        <AdminFlyoutPane
          open={!!selectedTypeId}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedTypeId(null);
              setFlyoutMode("view");
            }
          }}
          title={
            flyoutMode === "view" ? "View Location Type" : "Edit Location Type"
          }
          description={typedSelectedType?.slug || undefined}
          width="2xl"
          mode={flyoutMode}
          headerActions={
            flyoutMode === "view" ? (
              <>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700 text-white w-[110px]"
                >
                  Delete
                </Button>
                <Button onClick={handleEdit} size="sm" className="w-[110px]">
                  Edit
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFlyoutMode("view")}
                  className="w-[110px]"
                >
                  Cancel
                </Button>
                <Button onClick={handleSave} size="sm" className="w-[110px]">
                  Save
                </Button>
              </>
            )
          }
        >
          {loadingType && !typedSelectedType ? (
            <LoadingSpinner />
          ) : typedSelectedType ? (
            <FlyoutGridLayout>
              {/* Basic Information */}
              <FlyoutGridSection title="BASIC INFORMATION">
                <FlyoutGridRow
                  label="Name"
                  value={
                    flyoutMode === "edit" ? (
                      <Input
                        value={editFormData.name}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            name: e.target.value,
                          })
                        }
                        className="h-8 text-sm"
                      />
                    ) : (
                      <span>{typedSelectedType.name}</span>
                    )
                  }
                />
                <FlyoutGridRow
                  label="Slug"
                  value={
                    <span className="font-mono">{typedSelectedType.slug}</span>
                  }
                />
                <FlyoutGridRow
                  label="Description"
                  value={
                    flyoutMode === "edit" ? (
                      <Input
                        value={editFormData.description}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            description: e.target.value,
                          })
                        }
                        className="h-8 text-sm"
                      />
                    ) : (
                      <span>{typedSelectedType.description || "None"}</span>
                    )
                  }
                />
                <FlyoutGridRow
                  label="Locations Using"
                  value={
                    <span>
                      {typedSelectedType._count?.locations || 0} locations
                    </span>
                  }
                  isLast
                />
              </FlyoutGridSection>

              {/* Section Visibility - only sections visible globally (Section Visibility) are listed */}
              <FlyoutGridSection title="SECTION VISIBILITY (Type Defaults)">
                {visibleSectionKeys.length === 0 ? (
                  <FlyoutGridRow
                    label="Sections"
                    value={
                      <span className="text-gray-500 text-sm">
                        No sections are visible globally. Enable sections in
                        Platform section visibility first.
                      </span>
                    }
                    isLast
                  />
                ) : (
                  visibleSectionKeys.map((key, index) => (
                    <FlyoutGridRow
                      key={key}
                      label={SECTION_LABELS[key] ?? key}
                      value={
                        flyoutMode === "edit" ? (
                          <Switch
                            checked={
                              editFormData[
                              key as keyof typeof editFormData
                              ] as boolean
                            }
                            onCheckedChange={(checked) =>
                              setEditFormData({
                                ...editFormData,
                                [key]: checked,
                              })
                            }
                          />
                        ) : (
                          <span
                            className={
                              typedSelectedType[key]
                                ? "text-green-600"
                                : "text-gray-400"
                            }
                          >
                            {typedSelectedType[key] ? "Enabled" : "Disabled"}
                          </span>
                        )
                      }
                      isLast={index === visibleSectionKeys.length - 1}
                    />
                  ))
                )}
              </FlyoutGridSection>
            </FlyoutGridLayout>
          ) : null}
        </AdminFlyoutPane>
      </div>
    </DefaultLayout>
  );
};

export default AdminLocationTypesPage;
